import React from "react";
import PageHero from "../components/PageHero";
import CTABanner from "../components/CTABanner";
import { CERTIFICATIONS, IMAGES } from "../data/site";
import { BadgeCheck, Target, Compass, ShieldCheck } from "lucide-react";

const VALUES = [
  {
    icon: Target,
    title: "Execution Over Promise",
    desc: "We commit to scopes we can deliver — and we deliver them on schedule, with documentation that supports audit and oversight.",
  },
  {
    icon: ShieldCheck,
    title: "Accountability First",
    desc: "Defined points of contact, structured supervision, and clear field accountability for every site we serve.",
  },
  {
    icon: Compass,
    title: "Adaptive Operations",
    desc: "Our service model is structured to expand alongside client needs — across capability areas, sectors, and contract structures.",
  },
  {
    icon: BadgeCheck,
    title: "Contract-Ready Posture",
    desc: "Compliance awareness, procurement-friendly communication, and disciplined recordkeeping built into how we operate.",
  },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <PageHero
        eyebrow="About Street Society"
        title="A disciplined, service-oriented company built around execution and trust."
        intro="Street Society LLC is structured to support diverse operational needs across government, commercial, and institutional environments — and built to adapt as client requirements evolve."
        image="govBuilding"
      />

      <section className="section-y bg-white">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <p className="text-overline text-[#4B7055] mb-4">Who We Are</p>
            <h2 className="heading-section text-3xl sm:text-4xl lg:text-5xl text-[#0A1F35]">
              Service is operational work. We treat it that way.
            </h2>
            <div className="mt-6 space-y-5 text-[#4A5568] text-base sm:text-lg leading-relaxed">
              <p>
                Street Society LLC is a multi-capability service and solutions provider supporting
                facilities, grounds, equipment, and operational environments for federal, state, local,
                and commercial clients. We are organized around the way procurement actually works —
                clear scopes, documented procedures, and dependable delivery.
              </p>
              <p>
                Whether engaging directly with a contracting officer, partnering with a prime, or
                running scoped work for a facility operator, Street Society brings the operational
                discipline these environments require: accountable supervision, responsive
                coordination, and quality execution.
              </p>
              <p>
                Our certifications and designations reflect a long-term commitment to small and
                disadvantaged business participation in the government marketplace — and our service
                approach reflects an equally serious commitment to professional execution.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={IMAGES.fieldOps}
                alt="Field operations team"
                className="w-full h-[28rem] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-[#0A1F35]/10" />
            </div>
            <div className="mt-6 border-l-2 border-[#4B7055] pl-5">
              <p className="font-serif text-lg text-[#0A1F35] italic leading-snug">
                “Reliability is not a tagline. It’s a series of small, disciplined decisions made
                every day in the field.”
              </p>
              <p className="mt-3 text-xs text-overline text-[#4A5568]">Street Society Operating Principle</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-[#F5F7F9]" data-testid="about-values">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="text-overline text-[#4B7055] mb-4">What We Stand For</p>
            <h2 className="heading-section text-3xl sm:text-4xl text-[#0A1F35]">
              The operating principles that shape every engagement.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="bg-white p-8 card-hover"
                  data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center bg-[#0A1F35] text-white mb-5">
                    <Icon size={20} strokeWidth={1.75} />
                  </span>
                  <h3 className="font-serif text-lg font-bold text-[#0A1F35] mb-2">{v.title}</h3>
                  <p className="text-sm text-[#4A5568] leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-y bg-white" data-testid="about-certifications">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-overline text-[#4B7055] mb-4">Certifications & Designations</p>
              <h2 className="heading-section text-3xl sm:text-4xl text-[#0A1F35]">
                A diverse, contract-ready entity.
              </h2>
              <p className="mt-5 text-[#4A5568] leading-relaxed">
                Street Society holds and operates under designations that support set-aside,
                socioeconomic, and diverse procurement goals across federal, state, local, and
                commercial supplier programs.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
                {CERTIFICATIONS.map((c, i) => (
                  <li
                    key={c}
                    className="bg-white p-6 flex items-start gap-3"
                    data-testid={`cert-${i}`}
                  >
                    <BadgeCheck size={20} className="text-[#4B7055] mt-0.5" strokeWidth={2} />
                    <span className="text-[#1A2530] text-sm font-medium leading-snug">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
